import React, { useEffect, useState } from "react";
import API_URL from "../../services/api";
import "./Books.css";

const Books = () => {
  const [books, setBooks] = useState([]);
  const [search, setSearch] = useState("");
  const [genreFilter, setGenreFilter] = useState("");
  const [languageFilter, setLanguageFilter] = useState("");
  const [saleFilter, setSaleFilter] = useState("");
  const [loading, setLoading] = useState(true);

  // ============================
  // GENRES
  // ============================

  const genres = [
    "Fiction",
    "Non-fiction",
    "Fantasy",
    "Science Fiction",
    "Mystery",
    "Horror",
    "Romance",
    "Adventure",
    "Biography",
    "Autobiography",
    "Self-help",
    "History",
    "Physics",
    "Chemistry",
    "Biology",
    "Computer Science",
    "Algebra",
    "Geometry",
    "Calculus",
    "Statistics",
    "Geography",
    "Civics",
    "Economics",
    "Programming",
    "Web Development",
    "Networking",
    "Databases",
    "English",
    "Urdu",
    "Grammar",
    "Literature",
  ];

  // ============================
  // FETCH BOOKS
  // ============================

  const fetchBooks = async () => {
    try {
      setLoading(true);

      const res = await fetch(`${API_URL}/api/books`);
      const data = await res.json();

      setBooks(Array.isArray(data) ? data : []);
    } catch (err) {
      console.log("FETCH BOOKS ERROR:", err);
      alert("Unable to load books.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBooks();
  }, []);

  // ============================
  // FILTER BOOKS
  // ============================

  const filteredBooks = books.filter((book) => {
    const title = book.title || "";
    const author = book.author || "";
    const genre = book.genre || "";
    const language = book.language || "";

    const searchValue = search.toLowerCase();

    const matchSearch =
      title.toLowerCase().includes(searchValue) ||
      author.toLowerCase().includes(searchValue) ||
      genre.toLowerCase().includes(searchValue);

    const matchGenre =
      genreFilter === "" || genre === genreFilter;

    const matchLanguage =
      languageFilter === "" ||
      language === languageFilter;

    const matchSale =
      saleFilter === "" ||
      (saleFilter === "sale" &&
        Number(book.is_for_sale) === 1) ||
      (saleFilter === "notsale" &&
        Number(book.is_for_sale) === 0);

    return (
      matchSearch &&
      matchGenre &&
      matchLanguage &&
      matchSale
    );
  });

  // ============================
  // CSV DOWNLOAD
  // ============================

  const downloadCSV = () => {
    if (filteredBooks.length === 0) {
      alert("No books available.");
      return;
    }

    const headers = [
      "Book ID",
      "Title",
      "Author",
      "Genre",
      "Language",
      "Quantity",
      "Available Quantity",
      "Publisher",
      "Publication Year",
      "ISBN",
      "For Sale",
      "Price",
      "Stock For Sale",
    ];

    const rows = filteredBooks.map((book) => [
      book.book_id,
      book.title,
      book.author,
      book.genre,
      book.language,
      book.quantity,
      book.available_quantity,
      book.publisher,
      book.publication_year,
      book.isbn,
      Number(book.is_for_sale) === 1 ? "Yes" : "No",
      book.price || "",
      book.stock_for_sale || "",
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((row) =>
        row
          .map((field) =>
            `"${String(field ?? "").replace(/"/g, '""')}"`
          )
          .join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    const url = window.URL.createObjectURL(blob);

    const link = document.createElement("a");

    link.href = url;
    link.download = "Books.csv";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    window.URL.revokeObjectURL(url);
  };

  // ============================
  // EDIT BOOK
  // ============================

  const handleEdit = async (book) => {
    const newTitle = window.prompt(
      "Enter new title",
      book.title
    );

    if (newTitle === null) return;

    const newAuthor = window.prompt(
      "Enter new author",
      book.author
    );

    if (newAuthor === null) return;

    if (!newTitle.trim() || !newAuthor.trim()) {
      alert("Title and author cannot be empty.");
      return;
    }

    try {
      const res = await fetch(
        `${API_URL}/api/books/update/${book.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            title: newTitle.trim(),
            author: newAuthor.trim(),
            genre: book.genre,
            language: book.language,
            quantity: book.quantity,
            publisher: book.publisher,
            publicationYear: book.publication_year,
            isbn: book.isbn,
            description: book.description,
          }),
        }
      );

      const data = await res.json();

      if (!res.ok) {
        alert(data.message || "Unable to update book.");
        return;
      }

      alert(data.message || "Book Updated Successfully");

      fetchBooks();
    } catch (err) {
      console.log("UPDATE BOOK ERROR:", err);
      alert("Server Error");
    }
  };

  // ============================
  // DELETE BOOK
  // ============================

  const handleDelete = async (id) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this book?"
    );

    if (!confirmDelete) return;

    try {
      const res = await fetch(
        `${API_URL}/api/books/delete/${id}`,
        {
          method: "DELETE",
        }
      );

      const data = await res.json();

      if (!res.ok) {
        alert(data.message || "Unable to delete book.");
        return;
      }

      alert(data.message || "Book Deleted Successfully");

      fetchBooks();
    } catch (err) {
      console.log("DELETE BOOK ERROR:", err);
      alert("Server Error");
    }
  };

  // ============================
  // CLEAR FILTERS
  // ============================

  const clearFilters = () => {
    setSearch("");
    setGenreFilter("");
    setLanguageFilter("");
    setSaleFilter("");
  };

  // ============================
  // STATISTICS
  // ============================

  const totalBooks = books.length;

  const saleBooks = books.filter(
    (book) => Number(book.is_for_sale) === 1
  ).length;

  const availableBooks = books.filter(
    (book) => Number(book.available_quantity) > 0
  ).length;

  const totalQuantity = books.reduce(
    (total, book) =>
      total + Number(book.quantity || 0),
    0
  );

  // ============================
  // UI
  // ============================

  return (
    <div className="books-page">

      {/* HEADER */}

      <div className="books-header">
        <div>
          <h1>Books Inventory</h1>

          <p>
            Manage, search and monitor library books
          </p>
        </div>

        <button
          className="books-download-btn"
          onClick={downloadCSV}
        >
          ⬇ Download CSV
        </button>
      </div>

      {/* STATISTICS */}

      <div className="books-stats">

        <div className="books-stat-card">
          <div className="books-stat-icon">📚</div>

          <div>
            <h2>{totalBooks}</h2>
            <p>Total Books</p>
          </div>
        </div>

        <div className="books-stat-card">
          <div className="books-stat-icon">📖</div>

          <div>
            <h2>{totalQuantity}</h2>
            <p>Total Copies</p>
          </div>
        </div>

        <div className="books-stat-card">
          <div className="books-stat-icon">✅</div>

          <div>
            <h2>{availableBooks}</h2>
            <p>Available Books</p>
          </div>
        </div>

        <div className="books-stat-card">
          <div className="books-stat-icon">🛒</div>

          <div>
            <h2>{saleBooks}</h2>
            <p>For Sale</p>
          </div>
        </div>

      </div>

      {/* FILTER PANEL */}

      <div className="books-filter-panel">

        <div className="books-search-wrapper">
          <span>🔍</span>

          <input
            type="text"
            placeholder="Search by title, author or genre..."
            value={search}
            onChange={(e) =>
              setSearch(e.target.value)
            }
          />
        </div>

        <select
          value={genreFilter}
          onChange={(e) =>
            setGenreFilter(e.target.value)
          }
        >
          <option value="">All Genres</option>

          {genres.map((genre) => (
            <option
              key={genre}
              value={genre}
            >
              {genre}
            </option>
          ))}
        </select>

        <select
          value={languageFilter}
          onChange={(e) =>
            setLanguageFilter(e.target.value)
          }
        >
          <option value="">All Languages</option>
          <option value="English">English</option>
          <option value="Urdu">Urdu</option>
        </select>

        <select
          value={saleFilter}
          onChange={(e) =>
            setSaleFilter(e.target.value)
          }
        >
          <option value="">All Books</option>
          <option value="sale">For Sale</option>
          <option value="notsale">
            Not For Sale
          </option>
        </select>

        <button
          className="books-clear-btn"
          onClick={clearFilters}
        >
          Clear
        </button>

      </div>

      {/* RESULT COUNT */}

      <div className="books-result-bar">
        <span>
          Showing{" "}
          <strong>{filteredBooks.length}</strong>{" "}
          of <strong>{books.length}</strong> books
        </span>
      </div>

      {/* TABLE */}

      <div className="books-table-wrapper">

        {loading ? (
          <div className="books-message">
            <div className="books-loader"></div>
            <p>Loading books...</p>
          </div>
        ) : filteredBooks.length === 0 ? (
          <div className="books-message">
            <div className="books-empty-icon">
              📚
            </div>

            <h3>No Books Found</h3>

            <p>
              Try changing your search or filters.
            </p>
          </div>
        ) : (
          <table className="books-table">

            <thead>
              <tr>
                <th>Book ID</th>
                <th>Cover</th>
                <th>Title</th>
                <th>Author</th>
                <th>Genre</th>
                <th>Language</th>
                <th>Quantity</th>
                <th>Sale</th>
                <th>Available</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>

              {filteredBooks.map((book) => (

                <tr key={book.id}>

                  <td>
                    <span className="book-id">
                      {book.book_id}
                    </span>
                  </td>

                  <td>

                    <div className="book-cover">

                      {book.front_cover ? (
                        <img
                          src={`${API_URL}/uploads/books/${book.front_cover}`}
                          alt={book.title}
                        />
                      ) : (
                        <span>📖</span>
                      )}

                    </div>

                  </td>

                  <td>
                    <strong className="book-title">
                      {book.title}
                    </strong>
                  </td>

                  <td>
                    {book.author}
                  </td>

                  <td>
                    <span className="genre-badge">
                      {book.genre}
                    </span>
                  </td>

                  <td>
                    {book.language || "—"}
                  </td>

                  <td>
                    {book.quantity}
                  </td>

                  <td>

                    {Number(book.is_for_sale) === 1 ? (
                      <span className="sale-badge yes">
                        Yes
                      </span>
                    ) : (
                      <span className="sale-badge no">
                        No
                      </span>
                    )}

                  </td>

                  <td>

                    <span
                      className={
                        Number(
                          book.available_quantity
                        ) > 0
                          ? "availability available"
                          : "availability unavailable"
                      }
                    >
                      {book.available_quantity}
                    </span>

                  </td>

                  <td>

                    <div className="book-actions">

                      <button
                        className="book-edit-btn"
                        onClick={() =>
                          handleEdit(book)
                        }
                      >
                        Edit
                      </button>

                      <button
                        className="book-delete-btn"
                        onClick={() =>
                          handleDelete(book.id)
                        }
                      >
                        Delete
                      </button>

                    </div>

                  </td>

                </tr>

              ))}

            </tbody>

          </table>
        )}

      </div>

    </div>
  );
};

export default Books;
